/**
 * HTTP Client & Telemetry (Observabilidad de llamadas REST)
 * Registra cada petición HTTP con su latencia, status code, payload y respuesta.
 */
import { ApiCallLog } from '../models/types';
import { environment } from '../../../environments/environment';

export function getApiUrl(endpoint: string): string {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  const base = environment.apiUrl ? environment.apiUrl.replace(/\/+$/, '') : '';
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
}

export type ApiLogListener = (logs: ApiCallLog[]) => void;
const logListeners: Set<ApiLogListener> = new Set();
let apiLogs: ApiCallLog[] = [];

export const STORAGE_PREFIX = 'fm_real_api_';

export function subscribeToApiLogs(listener: ApiLogListener): () => void {
  logListeners.add(listener);
  listener([...apiLogs]);
  return () => {
    logListeners.delete(listener);
  };
}

export function clearApiLogs(): void {
  apiLogs = [];
  logListeners.forEach((l) => l([]));
}

function redact(value: any): any {
  if (Array.isArray(value)) return value.map(redact);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key,v]) =>
    [key, /token|invitation|password|authorization|cookie/i.test(key) ? '[redacted]' : redact(v)]));
  return value;
}

export async function recordApiCall<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  requestBody: any,
  responseProducer: () => Promise<T> | T,
  simulatedDelayMs: number = 0
): Promise<T> {
  const startTime = performance.now();
  if (simulatedDelayMs > 0) {
    await new Promise((resolve) => setTimeout(resolve, simulatedDelayMs));
  }

  let statusCode = 200;
  let responseData: any;
  try {
    responseData = await responseProducer();
    if (
      responseData &&
      typeof responseData === 'object' &&
      '_httpStatus' in responseData
    ) {
      statusCode = responseData._httpStatus;
      delete responseData._httpStatus;
    }
  } catch (err: any) {
    statusCode = err.status || 500;
    responseData = { error: err.message || 'Internal Server Error' };
    throw err;
  } finally {
    const durationMs = Math.round(performance.now() - startTime);
    const newLog: ApiCallLog = {
      id: `call-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      timestamp: new Date().toLocaleTimeString(),
      method,
      endpoint,
      statusCode,
      durationMs,
      requestBody: requestBody
        ? redact(requestBody)
        : undefined,
      responseBody: responseData
        ? redact(responseData)
        : undefined,
    };

    apiLogs = [newLog, ...apiLogs.slice(0, 49)];
    logListeners.forEach((l) => l([...apiLogs]));
  }

  return responseData;
}
